import merge from "deepmerge";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { getPythonBackendUrl } from "js/utils/getBackendUrl";

export function authHeaders() {
	return {
		Authorization: localStorage.getItem("TOKEN"),
		User: localStorage.getItem("USERNAME"),
		Device: localStorage.getItem("DEVICE"),
	};
}

export function createAuthHeaders(deviceId, username, token) {
	return {
		Authorization: token,
		User: username,
		Device: deviceId,
	};
}

export function defaultAuthFailureHandler(response, responseJson) {
	if (response.status === 403 || response.status === 401) {
		useUserSessionStore.getState().handleInvalidTokenError(responseJson);
	}
}

function parseOptions(options = {}) {
	const { unauthenticated = false, timeout = null, abortController = null } = options;
	let init = { headers: {} };

	const controller = abortController || new AbortController();
	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		init.signal = controller.signal;
	}
	if (abortController) {
		init.signal = controller.signal;
	}

	// In most cases, unauthenticated will be false and we will add authHeaders
	if (!unauthenticated) {
		init.headers = authHeaders();
	}
	init.headers["Content-Type"] = "application/json";

	if (options.init) {
		init = merge(init, options.init);
	}

	return init;
}

export class FetchStatusError extends Error {
	constructor(fetchInit, response, respJson) {
		super(`fetch failed with status ${response.statusText || response.status}`);
		this.name = "FetchStatusError";
		this.fetchInit = fetchInit;
		this.response = response;
		this.respJson = respJson;
	}
}

export const baseUrl = getPythonBackendUrl(window.location.hostname);
export async function fetch(resource, init = {}, insertBaseUrl = true, onAuthFailure = () => {}) {
	const response = await window.fetch(`${insertBaseUrl ? baseUrl : ""}${resource}`, init);

	if (!response.ok || (response.status >= 400 && response.status <= 599)) {
		let respJson = null;

		try {
			respJson = await response.json();
		} catch (err) {
			respJson = { error: "failed to decode the response body" };
		} finally {
			onAuthFailure(response, respJson);
			throw new FetchStatusError(init, response, respJson);
		}
	}

	let data;
	try {
		data = await response.json();
	} catch (err) {
		console.warn(`failed to json parse the response body from ${resource}, it may be empty or malformed`);
		data = null;
	}
	return data;
}

export async function fetchPlainText(resource, init = {}, insertBaseUrl = true, onAuthFailure = () => {}) {
	const response = await window.fetch(`${insertBaseUrl ? baseUrl : ""}${resource}`, init);

	if (!response.ok || (response.status >= 400 && response.status <= 599)) {
		let respJson = null;

		try {
			const respText = await response.text();
			respJson = { error: respText };
		} catch (err) {
			respJson = { error: "failed to decode the response body" };
		} finally {
			onAuthFailure(response, respJson);
			throw new FetchStatusError(init, response, respJson);
		}
	}

	return response.text();
}

function parseQueryParams(params) {
	if (!params || Object.keys(params).length === 0) {
		return "";
	}

	return Object.entries(params)
		.map(([key, value], i) => {
			if (Array.isArray(value)) {
				return value.map((v, j) => `${i || j ? "&" : "?"}${key}[]=${v}`).join("");
			}
			return `${i ? "&" : "?"}${key}=${value}`;
		})
		.join("");
}

export async function get(resource, options = {}, queryParams, onAuthFailure) {
	const insertBaseUrl = options.insertBaseUrl;
	delete options.insertBaseUrl;

	const init = parseOptions(options);
	const parsedQueryParams = parseQueryParams(queryParams);
	init.method = "GET";

	if (!onAuthFailure) {
		onAuthFailure = defaultAuthFailureHandler;
	}
	return fetch(`${resource}${parsedQueryParams}`, init, insertBaseUrl, onAuthFailure);
}

export async function post(resource, payload, options = {}) {
	const insertBaseUrl = options.insertBaseUrl;
	delete options.insertBaseUrl;

	const init = parseOptions(options);
	init.method = "POST";
	if (payload) {
		init.body = JSON.stringify(payload);
	}
	return fetch(resource, init, insertBaseUrl);
}

export async function put(resource, payload, options = {}) {
	const insertBaseUrl = options.insertBaseUrl;
	delete options.insertBaseUrl;

	const init = parseOptions(options);
	init.method = "PUT";
	if (payload) {
		init.body = JSON.stringify(payload);
	}
	return fetch(resource, init, insertBaseUrl);
}

export async function del(resource, payload = {}, options = {}, queryParams) {
	const insertBaseUrl = options.insertBaseUrl;
	delete options.insertBaseUrl;

	const init = parseOptions(options);
	const parsedQueryParams = parseQueryParams(queryParams);
	init.method = "DELETE";
	if (payload) {
		init.body = JSON.stringify(payload);
	}
	return fetch(`${resource}${parsedQueryParams}`, init, insertBaseUrl);
}

export async function fetchDownload(resource, init = {}, insertBaseUrl = true, onAuthFailure = () => {}) {
	fetchBlob(resource, init, insertBaseUrl, onAuthFailure).then((href) => {
		Object.assign(document.createElement("a"), { href, download: "Screenshots.zip" }).click();
	});
}

export async function fetchBlob(resource, init = {}, insertBaseUrl = true, onAuthFailure = () => {}) {
	return window
		.fetch(`${insertBaseUrl ? baseUrl : ""}${resource}`, init)
		.then((res) => res.blob())
		.then((blob) => URL.createObjectURL(blob));
}
